import {
  FREE_ROW_LIMIT,
  SNAPSHOT_STALE_MS,
  parsePublishedSnapshot,
  type PublishedSnapshot,
  type SnapshotRow,
} from '@afterhours/core/published';

/**
 * Everything this site knows, and how it comes to know it.
 *
 * There is no database here and no API of our own. The agent on the PC writes
 * one JSON file, uploads it, and this bundle reads it — so the entire
 * deployment is static files on a CDN, which is what makes it free to run.
 *
 * The types and the parser are imported from `@afterhours/core/published`
 * rather than redeclared, so the publisher and the reader cannot disagree
 * about the shape of a row. Nothing in this module is a React concern: it is
 * plain functions and a state machine, which is what makes it testable in the
 * repo's node-environment vitest without a DOM.
 *
 * Failure is a first-class state, not an exception. The snapshot is on another
 * host and will be unreachable sometimes; when that happens the last good
 * payload stays on screen with its real age beside it, because a stale number
 * that says how stale it is beats a blank page.
 */

export const POLL_MS = 30_000;
export { FREE_ROW_LIMIT, SNAPSHOT_STALE_MS };

/** Where the published snapshot lives. Baked in at build time by Vite. */
export function snapshotUrl(): string | null {
  const raw = import.meta.env['VITE_SNAPSHOT_URL'];
  const url = typeof raw === 'string' ? raw.trim() : '';
  return url === '' ? null : url;
}

export type SnapshotStatus =
  /** No payload yet and no verdict — the first fetch is in flight. */
  | 'loading'
  /** A payload is on screen. Whether it is fresh is `stale`, not this. */
  | 'ok'
  /** No payload was ever obtained. `error` says why. */
  | 'error'
  /** `VITE_SNAPSHOT_URL` was never set, so there is nothing to fetch. */
  | 'unconfigured';

export interface SnapshotState {
  status: SnapshotStatus;
  /** The last payload that parsed. Survives a failed refresh on purpose. */
  snapshot: PublishedSnapshot | null;
  /** Epoch ms of the last *successful* read, not of the last attempt. */
  fetchedAt: number | null;
  /** Set when the most recent attempt failed, even if a payload is showing. */
  error: string | null;
}

export function initialState(configured = snapshotUrl() !== null): SnapshotState {
  return {
    status: configured ? 'loading' : 'unconfigured',
    snapshot: null,
    fetchedAt: null,
    error: null,
  };
}

export type FetchResult =
  | { ok: true; snapshot: PublishedSnapshot }
  | { ok: false; error: string };

/**
 * Fold one fetch result into the state.
 *
 * The rule worth stating: a failure never clears a payload. Losing the network
 * for one poll must not blank a page that was correct thirty seconds ago — it
 * marks the error, keeps the rows, and lets the age readout tell the truth
 * about how old they are.
 */
export function applyResult(
  prev: SnapshotState,
  result: FetchResult,
  now = Date.now(),
): SnapshotState {
  if (result.ok) {
    return { status: 'ok', snapshot: result.snapshot, fetchedAt: now, error: null };
  }
  return {
    status: prev.snapshot ? 'ok' : 'error',
    snapshot: prev.snapshot,
    fetchedAt: prev.fetchedAt,
    error: result.error,
  };
}

/** Fetch and parse. Never throws — every failure comes back as a result. */
export async function fetchSnapshot(url: string, signal?: AbortSignal): Promise<FetchResult> {
  let res: Response;
  try {
    res = await fetch(url, { signal, cache: 'no-store' });
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'network error' };
  }
  if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: 'response was not JSON' };
  }
  const snapshot = parsePublishedSnapshot(body);
  if (!snapshot) return { ok: false, error: 'snapshot did not parse' };
  return { ok: true, snapshot };
}

/** True once the payload is older than the agent's own staleness threshold. */
export function isStale(generatedAt: number | null, now = Date.now()): boolean {
  return generatedAt === null || now - generatedAt >= SNAPSHOT_STALE_MS;
}

/**
 * How old the data is, in words. Shown beside every headline figure so a
 * reader never has to guess whether the page is live.
 */
export function ageLabel(generatedAt: number | null, now = Date.now()): string {
  if (generatedAt === null) return 'no data';
  const sec = Math.max(0, Math.round((now - generatedAt) / 1000));
  if (sec < 90) return 'just now';
  if (sec < 5400) return `${Math.round(sec / 60)}m ago`;
  if (sec < 172800) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

export interface FreeSlice {
  visible: SnapshotRow[];
  lockedSymbols: string[];
  lockedCount: number;
}

/**
 * The free tier's cut of the rows.
 *
 * Be honest about what this is: the published JSON carries every row, because
 * the publisher does not know who is asking. So this cap is presentation, not
 * enforcement — anyone can open the network tab. The real paywall is the x402
 * API (see deploy/README.md), which is a server and can refuse. Calling this a
 * paywall in copy would be a claim we cannot back, and this project does not
 * make those.
 */
export function freeSlice(rows: SnapshotRow[], limit = FREE_ROW_LIMIT): FreeSlice {
  const visible = rows.slice(0, limit);
  const locked = rows.slice(limit);
  return {
    visible,
    lockedSymbols: locked.map((r) => r.symbol),
    lockedCount: locked.length,
  };
}
